/* GASSISA
========================================================================================
* Descripción: Modal para búsqueda y reimpresión de tickets de caja
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import { Fragment, useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tabs,
    Tab,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
} from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import {
    apiBuscarAperturas,
    apiBuscarCierres,
    apiBuscarRetiros,
    apiListadoVentasCaja,
    apiObtenerListaCotizaciones,
    apiObtenerRemisionVenta
} from "../../settings/ApiConfig";
import { buildCierreCajaTicket, buildCierreCajaTicketRows, buildCierreCajaTicketText, buildTicketBytes } from "./cajaTicketUtils";
import { buildTicketVentaParts, layoutTieneObjetos, normalizarDataTicketCotizacion } from "../../settings/ticketVentaEscape";
import { buildTicketCotizacionClasicoParts } from "../../settings/ticketVentaClasico";
import { imprimirTicketEnPrinterTools } from "../../settings/ticketImpresion";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
    }).format(value || 0);
};

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString('es-MX');
};

const formatDateOnly = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString('es-MX');
};

const formatDateTimeQuery = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

export default function ModalReimpresionCaja(props) {
    const { openModal = false, onClose, tipo = 'apertura', caja } = props;
    
    const { handleOpenAlert } = useLayout();
    const { GetRequest } = useRequest();
    const { parametrosCajaForm } = useSesion();

    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [aperturas, setAperturas] = useState([]);
    const [cierres, setCierres] = useState([]);
    const [retiros, setRetiros] = useState([]);
    const [ventas, setVentas] = useState([]);
    const [cotizaciones, setCotizaciones] = useState([]);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (openModal) {
            if (tipo === 'remision') setTab(0);
            else if (tipo === 'cotizacion') setTab(1);
            else if (tipo === 'apertura') setTab(2);
            else if (tipo === 'corte' || tipo === 'cierre') setTab(3);
            else setTab(4);
        }
    }, [openModal, tipo]);

    useEffect(() => {
        if (openModal) {
            const hoy = new Date();
            setFechaInicio(hoy);
            setFechaFin(hoy);
            buscarDatos(hoy, hoy);
        }
    }, [openModal, caja]);

    useEffect(() => {
        if (openModal) {
            buscarDatos(fechaInicio, fechaFin);
        }
    }, [tab]);

    const buscarDatos = async (fechaIni, fechaF) => {
        if (!caja) {
            return;
        }

        const fi = fechaIni || fechaInicio;
        const ff = fechaF || fechaFin;

        setLoading(true);
        const cajaValue = caja.pc ?? caja.PC ?? caja.value ?? caja.label ?? caja;

        setVentas([]);
        setCotizaciones([]);

        try {
            switch (tab) {
                case 0: {
                    const params = new URLSearchParams();
                    params.append('fechainicial', formatDateTimeQuery(fi));
                    params.append('fechafinal', formatDateTimeQuery(ff));
                    const urlVentas = `${apiListadoVentasCaja.replace('{pc}', cajaValue.toUpperCase())}?${params.toString()}`;
                    const respVentas = await GetRequest({ url: urlVentas });
                    if (!respVentas.hasError && respVentas.result) {
                        setVentas(respVentas.result || []);
                    }
                    break;
                }
                case 1: {
                    const params = new URLSearchParams();
                    params.append('fechaInicio', fi.toISOString());
                    params.append('fechaFin', ff.toISOString());
                    const urlCotizaciones = `${apiObtenerListaCotizaciones.replace('{caja}', cajaValue)}?${params.toString()}`;
                    const respCotizaciones = await GetRequest({ url: urlCotizaciones });
                    if (!respCotizaciones.hasError) {
                        setCotizaciones(respCotizaciones.result || []);
                    }
                    break;
                }
                case 2: {
                    const params = new URLSearchParams();
                    params.append('fechaInicio', fi.toISOString());
                    params.append('fechaFin', ff.toISOString());
                    const respAperturas = await GetRequest({
                        url: `${apiBuscarAperturas.replace('{caja}', cajaValue)}?${params.toString()}`
                    });
                    if (!respAperturas.hasError) {
                        setAperturas(respAperturas.result || []);
                    }
                    break;
                }
                case 3: {
                    const params = new URLSearchParams();
                    params.append('fechaInicio', fi.toISOString());
                    params.append('fechaFin', ff.toISOString());
                    const respCierres = await GetRequest({
                        url: `${apiBuscarCierres.replace('{caja}', cajaValue)}?${params.toString()}`
                    });
                    if (!respCierres.hasError) {
                        setCierres(respCierres.result || []);
                    }
                    break;
                }
                case 4: {
                    const params = new URLSearchParams();
                    params.append('fechaInicio', fi.toISOString());
                    params.append('fechaFin', ff.toISOString());
                    const respRetiros = await GetRequest({
                        url: `${apiBuscarRetiros.replace('{caja}', cajaValue)}?${params.toString()}`
                    });
                    if (!respRetiros.hasError) {
                        setRetiros(respRetiros.result || []);
                    }
                    break;
                }
            }
        } catch (error) {
            handleOpenAlert("Error al buscar datos: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBuscar = () => {
        buscarDatos(fechaInicio, fechaFin);
    };

    const handleHoy = () => {
        const hoy = new Date();
        setFechaInicio(hoy);
        setFechaFin(hoy);
        buscarDatos(hoy, hoy);
    };

    const agregarDesglosePreview = (lines, monedas) => {
        const lista = monedas || [];
        if (lista.length === 0) {
            return;
        }
        lines.push("");
        lines.push("DESGLOSE DE EFECTIVO:");
        lines.push("-".repeat(32));
        lista.forEach(m => {
            if (m.cantidad > 0) {
                const subtotal = m.cantidad * m.valor;
                const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                lines.push(`${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}`);
            }
        });
        lines.push("-".repeat(32));
    };

    const construirVistaPrevia = (tipo, data) => {
        const lines = [];
        if (tipo === "apertura") {
            lines.push("APERTURA DE CAJA");
            lines.push("-".repeat(32));
            lines.push(`Caja: ${data.caja || ""}`);
            lines.push(`Usuario: ${data.usuario || ""}`);
            lines.push(`Fecha: ${formatDate(data.fechaapertura)}`);
            lines.push("-".repeat(32));
            lines.push("");
            lines.push(`TOTAL: ${formatCurrency(data.montoinicial)}`);
            agregarDesglosePreview(lines, data.listacantidadmonedasbilletes);
            if (data.observaciones) lines.push(`Obs: ${data.observaciones}`);
            return lines;
        }
        if (tipo === "retiro") {
            lines.push("RETIRO DE CAPITAL");
            lines.push("-".repeat(32));
            lines.push(`Caja: ${data.caja || ""}`);
            lines.push(`Usuario: ${data.usuarioautorizado || ""}`);
            lines.push(`Fecha: ${formatDate(data.fecharetiro)}`);
            lines.push("-".repeat(32));
            lines.push("");
            lines.push(`TOTAL RETIRO: ${formatCurrency(data.montoretiro)}`);
            agregarDesglosePreview(lines, data.listacantidadmonedasbilletes);
            if (data.observaciones) lines.push(`Obs: ${data.observaciones}`);
            return lines;
        }
        if (tipo === "cierre") {
            lines.push("CIERRE DE CAJA");
            lines.push("-".repeat(32));
            lines.push(`Caja: ${data.caja || ""}`);
            lines.push(`Usuario: ${data.usuario || ""}`);
            lines.push(`Fecha: ${formatDate(data.fechacierre)}`);
            lines.push("-".repeat(32));
            lines.push("");
            lines.push("RESUMEN DE CORTE:");
            lines.push("-".repeat(32));
            const { rows } = buildCierreCajaTicketRows(data);
            buildCierreCajaTicketText(rows, formatCurrency).forEach(l => lines.push(l));
            lines.push("-".repeat(32));
            agregarDesglosePreview(lines, data.listacantidadmonedasbilletes);
            if (data.observaciones) lines.push(`Obs: ${data.observaciones}`);
            return lines;
        }
        return lines;
    };

    const handleImprimirPreview = () => {
        if (!preview) return;
        const { tipo, data } = preview;
        if (tipo === "apertura") imprimirTicketApertura(data);
        else if (tipo === "cierre") imprimirTicketCierre(data);
        else if (tipo === "retiro") imprimirTicketRetiro(data);
        setPreview(null);
    };

    const imprimirTicketApertura = async (data) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'APERTURA DE CAJA\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (data.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (data.usuario || "") + '\n'),
            textEncoder.encode('Fecha: ' + formatDate(data.fechaapertura) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        const monedas = data.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push('-'.repeat(32) + '\n');
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL: ' + formatCurrency(data.montoinicial) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (data.observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION APERTURA\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            handleOpenAlert("Logo no disponible:", err);
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketCierre = async (data) => {
        const textEncoder = new TextEncoder();

        const ticketParts = buildCierreCajaTicket({
            textEncoder,
            data,
            formatCurrency,
            headerDate: formatDate(data.fechacierre),
        });
        const finalTicket = buildTicketBytes(ticketParts);

        const formData = new FormData();
        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            handleOpenAlert("Logo no disponible:", err);
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketRetiro = async (data) => {
        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [textEncoder.encode('\x1B\x40')];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'RETIRO DE CAPITAL\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (data.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (data.usuarioautorizado || "") + '\n'),
            textEncoder.encode('Fecha: ' + formatDate(data.fecharetiro) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n\n'),
        );

        const monedas = data.listacantidadmonedasbilletes || [];
        if (monedas.length > 0) {
            ticketParts.push(textEncoder.encode('DESGLOSE DE EFECTIVO:\n'));
            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n'));
            
            monedas.forEach(m => {
                if (m.cantidad > 0) {
                    const subtotal = m.cantidad * m.valor;
                    const label = m.tipo === 'MONEDA' ? 'Mda' : 'Bll';
                    ticketParts.push(textEncoder.encode(
                        `${label} $${m.valor.toFixed(2)} x ${m.cantidad} = $${subtotal.toFixed(2)}\n`
                    ));
                }
            });
            
            ticketParts.push('-'.repeat(32) + '\n');
        }

        ticketParts.push(
            textEncoder.encode('\n'),
            textEncoder.encode('\x1B\x21\x11' + 'TOTAL RETIRO: ' + formatCurrency(data.montoretiro) + '\n'),
            textEncoder.encode('\x1B\x21\x00'),
        );

        if (data.observaciones) {
            ticketParts.push(textEncoder.encode('\nObs: ' + data.observaciones + '\n'));
        }

        ticketParts.push(
            textEncoder.encode('\n\n'),
            textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION RETIRO\n\n\n'),
            textEncoder.encode('\x1B\x64\x06'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            handleOpenAlert("Logo no disponible:", err);
        }

        try {
            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });
            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketRemision = async (remision, caja) => {
        const cajaValue = caja || (caja.pc ?? caja.PC ?? caja.value ?? caja.label ?? caja);
        try {
            const url = apiObtenerRemisionVenta
                .replace("{remision}", remision)
                .replace("{caja}", cajaValue);
            
            const response = await GetRequest({ url });
            
            if (response.hasError || !response.result) {
                handleOpenAlert("No se pudo obtener la remisión", "error");
                return;
            }

            const dataTicket = response.result;

            if (layoutTieneObjetos(parametrosCajaForm)) {
                const { partes } = buildTicketVentaParts({
                    dataTicket,
                    parametrosCajaForm,
                    tipoDocumento: "VENTA",
                    etiquetaFolio: "Remision",
                    tituloDocumento: "REMISION DE VENTA",
                    esReimpresion: true,
                    despedida: "Gracias por su compra!",
                });
                await imprimirTicketEnPrinterTools({
                    partes,
                    parametrosCajaForm,
                    nombreImpresora: parametrosCajaForm?.nombreimpresoraventas?.trim() || null,
                });
                handleOpenAlert("Reimpresion realizada", "OK");
                return;
            }

            const formData = new FormData();
            const textEncoder = new TextEncoder();

            let ticketParts = [textEncoder.encode('\x1B\x40')];

            ticketParts.push(
                textEncoder.encode('\x1B\x61\x01\x1B\x21\x10' + 'REMISION DE VENTA\n'),
                textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
                textEncoder.encode('Folio: ' + remision + '\n'),
                textEncoder.encode('Caja: ' + (cajaValue || "") + '\n'),
                textEncoder.encode('Fecha: ' + formatDate(dataTicket?.cabecero?.fechaventa) + '\n'),
            );

            if (dataTicket?.cabecero?.razonsocial) {
                ticketParts.push(textEncoder.encode('Cliente: ' + dataTicket.cabecero.razonsocial + '\n'));
            }

            ticketParts.push(textEncoder.encode('-'.repeat(32) + '\n\n'));

            const detalles = dataTicket?.detalles || [];
            let subtotal = 0;
            let totalIVA = 0;
            let total = 0;

            detalles.forEach((item) => {
                const descripcion = (item.descripcion || "").substring(0, 20);
                const cantidad = item.cantidad || 0;
                const precio = item.costocliente || 0;
                const importe = cantidad * precio;
                subtotal += importe;
                totalIVA += item.iva || 0;
                total += item.total || 0;

                ticketParts.push(textEncoder.encode(
                    `${descripcion}\n${cantidad} x $${precio.toFixed(2)} = $${importe.toFixed(2)}\n`
                ));
            });

            ticketParts.push(
                textEncoder.encode('-'.repeat(32) + '\n'),
                textEncoder.encode(`Subtotal: $${subtotal.toFixed(2)}\n`),
                textEncoder.encode(`IVA: $${totalIVA.toFixed(2)}\n`),
                textEncoder.encode('\x1B\x21\x11' + `TOTAL: $${total.toFixed(2)}\n`),
                textEncoder.encode('\x1B\x21\x00'),
            );

            ticketParts.push(
                textEncoder.encode('\n\n'),
                textEncoder.encode('\x1B\x61\x01' + 'REIMPRESION VENTA\n\n\n'),
                textEncoder.encode('\x1B\x64\x06'),
                textEncoder.encode('\x1D\x56\x00')
            );

            const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
            const finalTicket = new Uint8Array(totalLength);
            let offset = 0;
            ticketParts.forEach(part => {
                finalTicket.set(part, offset);
                offset += part.length;
            });

            formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
            formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

            const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
            try {
                const logoResponse = await fetch(logoUrl);
                if (logoResponse.ok) {
                    const logoBlob = await logoResponse.blob();
                    formData.append("logo", logoBlob, "logocliente.png");
                }
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }

            await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            handleOpenAlert("Ticket reimpreso", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir ticket: " + error.message, "error");
        }
    };

    const imprimirTicketCotizacion = async (folioCotizacion) => {
        const cajaValue = caja.pc ?? caja.PC ?? caja.value ?? caja.label ?? caja;
        try {
            const url = `${process.env.REACT_APP_ServerSubProcess}/Ventas/obtenercotizacion/${folioCotizacion}/${cajaValue}`;

            const response = await GetRequest({ url });

            if (response.hasError || !response.result) {
                handleOpenAlert("No se pudo obtener la cotización", "error");
                return;
            }

            const dataTicket = normalizarDataTicketCotizacion({
                dataTicket: response.result,
                folioCotizacion,
                caja: cajaValue,
                parametrosCajaForm,
            });

            const { partes } = layoutTieneObjetos(parametrosCajaForm)
                ? buildTicketVentaParts({
                    dataTicket,
                    parametrosCajaForm,
                    tipoDocumento: "COTIZACION",
                    etiquetaFolio: "Cotizacion",
                    tituloDocumento: "COTIZACION",
                    esReimpresion: true,
                })
                : buildTicketCotizacionClasicoParts({
                    dataTicket: response.result,
                    folioCotizacion,
                    caja: cajaValue,
                });

            await imprimirTicketEnPrinterTools({
                partes,
                parametrosCajaForm,
                nombreImpresora: parametrosCajaForm?.nombreimpresoracotizaciones?.trim() || null,
            });

            handleOpenAlert("Cotización reimpresa", "success");
        } catch (error) {
            handleOpenAlert("Error al reimprimir cotización: " + error.message, "error");
        }
    };

    return (
        <Modal
            open={openModal}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80vw',
                maxWidth: 900,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 3,
                borderRadius: 2,
                overflow: 'auto',
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Reimpresión de Tickets de Caja
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'end', mb: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="caption" display="block" gutterBottom>Fecha Inicio</Typography>
                        <DatePicker
                            selected={fechaInicio}
                            onChange={(d) => setFechaInicio(d)}
                            dateFormat="dd/MM/yyyy"
                            className="custom-datepicker"
                        />
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block" gutterBottom>Fecha Fin</Typography>
                        <DatePicker
                            selected={fechaFin}
                            onChange={(d) => setFechaFin(d)}
                            dateFormat="dd/MM/yyyy"
                            className="custom-datepicker"
                            minDate={fechaInicio}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleBuscar}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Buscar
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleHoy}
                        disabled={loading}
                    >
                        Hoy
                    </Button>
                </Box>

                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Remisiones" />
                    <Tab label="Cotizaciones" />
                    <Tab label="Aperturas" />
                    <Tab label="Cierres" />
                    <Tab label="Retiros" />
                </Tabs>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Remisión</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Vendedor</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tab === 0 && ventas.map((row) => (
                                    <TableRow key={row.remision}>
                                        <TableCell>{row.remision}</TableCell>
                                        <TableCell>{formatDate(row.fechaventa)}</TableCell>
                                        <TableCell>{row.razonsocial || "PUBLICO GENERAL"}</TableCell>
                                        <TableCell>{row.usuario}</TableCell>
                                        <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => imprimirTicketRemision(row.remision, row.pc)}>
                                                <PrintIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tab === 1 && cotizaciones.map((row, index) => (
                                    <TableRow key={`${row.remision}-${index}`}>
                                        <TableCell>{row.remision}</TableCell>
                                        <TableCell>{formatDate(row.fecharegistro)}</TableCell>
                                        <TableCell>{row.razonsocial}</TableCell>
                                        <TableCell>{row.pc}</TableCell>
                                        <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => imprimirTicketCotizacion(row.remision)}>
                                                <PrintIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tab === 2 && aperturas.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{formatDate(row.fechaapertura)}</TableCell>
                                        <TableCell>{row.usuario}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell align="right">{formatCurrency(row.montoinicial)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" title="Vista previa" onClick={() => setPreview({ tipo: "apertura", data: row })}>
                                                <PreviewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tab === 3 && cierres.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.foliocorte}</TableCell>
                                        <TableCell>{formatDate(row.fechacierre)}</TableCell>
                                        <TableCell>{row.usuario}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell align="right">{formatCurrency(row.montocierre)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" title="Vista previa" onClick={() => setPreview({ tipo: "cierre", data: row })}>
                                                <PreviewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tab === 4 && retiros.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{formatDate(row.fecharetiro)}</TableCell>
                                        <TableCell>{row.usuarioautorizado}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell align="right">{formatCurrency(row.montoretiro)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" title="Vista previa" onClick={() => setPreview({ tipo: "retiro", data: row })}>
                                                <PreviewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tab === 0 && ventas.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay remisiones</TableCell>
                                    </TableRow>
                                )}
                                {tab === 1 && cotizaciones.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay cotizaciones</TableCell>
                                    </TableRow>
                                )}
                                {tab === 2 && aperturas.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay aperturas</TableCell>
                                    </TableRow>
                                )}
                                {tab === 3 && cierres.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay cierres</TableCell>
                                    </TableRow>
                                )}
                                {tab === 4 && retiros.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay retiros</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cerrar
                    </Button>
                </Box>
                <Dialog
                    open={Boolean(preview)}
                    onClose={() => setPreview(null)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Vista previa del ticket</DialogTitle>
                    <DialogContent dividers>
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'Consolas, monospace',
                                fontSize: 13,
                                lineHeight: 1.4,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0,
                            }}
                        >
                            {preview ? construirVistaPrevia(preview.tipo, preview.data).join("\n") : ""}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPreview(null)}>Cerrar</Button>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handleImprimirPreview}
                        >
                            Imprimir
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Modal>
    );
}
